import { Component, OnInit } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import {
  catchError,
  filter,
  finalize,
  map,
  of,
  switchMap,
  take,
  tap,
  throwError,
} from 'rxjs';
import { PolicyService } from 'src/app/modules/policy/policy.service';
import { BasicModalComponent } from 'src/app/modules/shared/components/basic-modal/basic-modal.component';
import {
  EModalOutput,
  NO_BUTTON,
  UNDERSTOOD_BUTTON,
  YES_BUTTON,
} from 'src/app/modules/shared/components/basic-modal/basic-modal.constants';
import { ITableKey } from 'src/app/modules/shared/components/basic-table/basic-table.constants';
import { IPaginationEvent } from 'src/app/modules/shared/components/be-paginated-table/be-paginated-table.constants';
import { IFormKey } from 'src/app/modules/shared/models/forms';
import {
  IEmployer,
  IEmployerSearchRequest,
} from 'src/app/modules/shared/models/operation.model';
import { EmployerSearchDatasource } from 'src/app/pages/dashboard/EmployerSearch.dataSource';
import { SearchService } from 'src/app/pages/search/search.service';
import { LoggerService } from 'src/app/services/logger.service';
import { SpinnerService } from 'src/app/services/spinner.service';
import { CommonUtils } from 'src/app/utils/CommonUtils';
import { FormUtils } from 'src/app/utils/FormUtils';
import { ModifyReferentEmployerComponent } from '../modify-referent-employer/modify-referent-employer.component';
import {
  MISSING_EMPLOYER_CODE_MESSAGE,
  prev_search_employer_form_keys,
  prev_search_employer_form_template,
  prev_search_employer_form_validators,
  prev_search_employer_table_modal_keys,
} from './employer-agency-search-form.constants';
import { AlertService } from 'src/app/components/alert/alert.service';

@Component({
  selector: 'app-employer-agency-search-form',
  templateUrl: './employer-agency-search-form.component.html',
  styleUrl: './employer-agency-search-form.component.scss',
})
export class EmployerAgencySearchComponent implements OnInit {
  public searchEmployerForm: FormGroup;
  public searchEmployerFormKeys: IFormKey[];

  public dataSource: EmployerSearchDatasource;
  public tableKeys: ITableKey[];
  public isSearchDisabled: boolean = true;
  public isEmployerSelectionConfirmDisabled: boolean;
  public hasSearched: boolean;
  public chosenEmployer: IEmployer;

  constructor(
    private searchService: SearchService,
    private policyService: PolicyService,
    private spinnerService: SpinnerService,
    private alertService: AlertService,
    public dialogRef: MatDialogRef<EmployerAgencySearchComponent>,
    private dialog: MatDialog,
    private logger: LoggerService
  ) {}

  ngOnInit(): void {
    this.searchEmployerForm = FormUtils.initFormGroupFromTemplate(
      prev_search_employer_form_template,
      prev_search_employer_form_validators
    );
    this.searchEmployerFormKeys = CommonUtils.assign(
      prev_search_employer_form_keys
    );

    this.dataSource = new EmployerSearchDatasource(
      this.searchService,
      this.spinnerService
    );
    this.tableKeys = CommonUtils.assign(prev_search_employer_table_modal_keys);

    this.searchEmployerForm.valueChanges.subscribe(() => {
      this.isSearchDisabled = Object.values(
        this.searchEmployerForm.value
      ).every((value) => !value);
    });

    this.isEmployerSelectionConfirmDisabled = true;
    this.hasSearched = false;
  }

  public reset(): void {
    this.searchEmployerForm.reset();
  }

  public searchEmployer() {
    this.performSearch();
    this.hasSearched = true;
  }

  public createNewEmployerCode(): void {
    LoggerService.logInfo(
      this.constructor.name,
      'Creating new employer code...'
    );

    const requestBody = {
      agencyRegistryNumber: this.chosenEmployer.registryId,
      policyNumber: this.policyService.policyId,
      productCode: this.policyService.productCode,
    };

    this.dialog
      .open(
        BasicModalComponent,
        this.chosenEmployer.employerCode
          ? {
              data: {
                title: 'Gestione Datore di Lavoro',
                text: 'Il codice datore di lavoro è già stato creato.',
                modalSize: 'modal-box-small align-items-center text-center',
                buttons: [UNDERSTOOD_BUTTON],
              },
            }
          : {
              data: {
                title: 'Gestione Datore di Lavoro',
                text: 'Vuoi creare un nuovo codice datore di lavoro?',
                modalSize: 'modal-box-small align-items-center text-center',
                buttons: [NO_BUTTON, YES_BUTTON],
              },
            }
      )
      .afterClosed()
      .pipe(
        filter((result) => result === EModalOutput.YES),
        switchMap(() => {
          this.spinnerService.show();
          return of(null);
        }),
        switchMap(() =>
          this.policyService.createEmployerCode(requestBody).pipe(
            catchError((error) => {
              LoggerService.logError(
                this.constructor.name,
                'createEmployerCode',
                {
                  error,
                }
              );
              this.spinnerService.hide();
              this.alertService.openErrorMessage(
                'Errore nella creazione del codice datore.',
                'createEmployerCode',
                error
              );
              return throwError(() => error);
            })
          )
        ),
        switchMap((response) => {
          LoggerService.logInfo(this.constructor.name, 'createEmployerCode', {
            response,
          });

          this.spinnerService.hide();

          return this.dialog
            .open(ModifyReferentEmployerComponent, {
              data: {
                employerCode: response.employerCode,
              },
              minWidth: '70vw',
              minHeight: '55vh',
            })
            .afterClosed()
            .pipe(
              take(1),
              map((outcome) => ({
                outcome,
                employerCode: response.employerCode,
              }))
            );
        }),
        switchMap(({ outcome, employerCode }) => {
          if (outcome && outcome.command === EModalOutput.CONFIRM) {
            this.spinnerService.show();

            return this.policyService
              .saveAgencyReferent({
                ...outcome.data,
                employerCode,
                policyNumber: this.policyService.policyId,
                registryId: this.chosenEmployer.registryId,
              })
              .pipe(
                tap(() => {
                  this.chosenEmployer.employerCode = employerCode;
                  this.spinnerService.hide();
                }),
                catchError((error) => {
                  this.spinnerService.hide();
                  LoggerService.logError(
                    this.constructor.name,
                    'saveAgencyReferent',
                    {
                      error,
                    }
                  );
                  this.alertService.openErrorMessage(
                    'Errore nel salvataggio del referente',
                    'saveAgencyReferent',
                    error
                  );
                  return throwError(() => error);
                })
              );
          }
          return of(null);
        }),
        finalize(() => {
          this.spinnerService.hide();
        }),
      )
      .subscribe({
        next: (response) => {
          if (response) {
            LoggerService.logInfo(this.constructor.name, 'createEmployerCode', {
              response,
            });
          }
        },
        error: (error) => {
          LoggerService.logError(this.constructor.name, 'createEmployerCode', {
            error,
          });
        },
      });
  }

  public onChangeRequested(event: IPaginationEvent) {
    console.warn('Change requested:', event);
    this.performSearch(event);
  }

  private performSearch(event?: IPaginationEvent) {
    let searchParams: IEmployerSearchRequest = this.searchEmployerForm.value;
    if (event) {
      searchParams = {
        ...searchParams,
        ...event,
      };
    }

    this.dataSource
      .search({
        ...searchParams,
        policyNumber: this.policyService.policyId,
      } as IEmployerSearchRequest)
      .subscribe({
        next: (response: IEmployer[]) => {
          this.dataSource.resultSubject.next(
            response.map((item) => ({
              ...item,
              id: item.employerCode, // Assuming employerCode is unique
              // employerCode: '', // FIXME: Debugging purpose, remove later
            }))
          );
          this.dataSource.length = response?.length || 0;
        },
        error: (error) => {
          console.error('Error fetching employer data:', error);
          this.dataSource.resultSubject.next([]);
          this.dataSource.length = 0;
        },
      });
  }

  public onItemSelected(item: string) {
    console.warn('Item selected:', item);
    this.isEmployerSelectionConfirmDisabled = false;
    this.chosenEmployer = this.dataSource.resultSubject.value.find((empl) => {
      return empl.id === item;
    });
  }

  public onConfirmSelection() {
    if (this.chosenEmployer && !this.chosenEmployer.employerCode) {
      this.dialog.open(BasicModalComponent, {
        data: {
          title: 'Gestione Datore di Lavoro',
          text: MISSING_EMPLOYER_CODE_MESSAGE,
          modalSize: 'modal-box-small align-items-center text-center',
          buttons: [UNDERSTOOD_BUTTON],
        },
      });
    } else {
      this.dialogRef.close({
        data: this.chosenEmployer,
        command: EModalOutput.CONFIRM,
      });
    }
  }

  public onCancelSelection() {
    this.dialogRef.close();
  }
}
